import React, {
    FunctionComponent,
    ReactElement,
    useCallback,
    useEffect,
    useState,
} from "react";

import {
    Button,
    Chip,
    IconButton,
    Snackbar as MuiSnackbar,
    SnackbarCloseReason,
    TextField,
    Typography,
    styled,
} from "@mui/material";

import { Add, Close, FilterList } from "@mui/icons-material";

import {
    DataGrid,
    GridColDef,
    GridRenderCellParams,
    GridRowParams,
    GridRowSelectionModel,
    GridValueGetterParams,
} from "@mui/x-data-grid";
import { formatDistance } from "date-fns";
import { useQueryClient } from "react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { EmptyRecords, StatusChip } from "../../components/common";
import { DataGridFooter } from "../../components/common";
import { useDeletePriceRule, useGetAllPriceRule } from "../../hooks";
import { IDiscountCode } from "../../sdk/types";
import { IPage } from "../../types";
import {
    PriceRuleCategoryNames,
    PriceRuleCategoryType,
    PriceRuleStatus,
    priceRuleStatusNames,
} from "../../utils/enums";

import { FilterDialog } from "./FilterDialog";

const Root = styled("main")`
    padding: ${({ theme }) => theme.spacing(3)};
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(3)};
`;

const TitleContainer = styled("div")`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const SearchContainer = styled("div")`
    display: flex;
    gap: ${({ theme }) => theme.spacing(2)};
    align-items: center;
`;

const FiltersContainer = styled("div")`
    display: flex;
    gap: ${({ theme }) => theme.spacing(1)};
    flex-wrap: wrap;
    align-items: center;
`;

const Title = styled(Typography)`
    font-size: 24px;
    font-weight: 500;
`;

const EmptyRecordsContainer = styled("div")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(1)};
    align-items: center;
`;

const Snackbar = styled(MuiSnackbar)`
    & .MuiPaper-root {
        background-color: ${({ theme }) => theme.palette.common.white};
        color: ${({ theme }) => theme.palette.common.black};
    }
`;

const StyledDataGrid = styled(DataGrid)`
    .MuiDataGrid-cell:focus {
        outline: none;
    }
    .MuiDataGrid-row:hover {
        cursor: pointer;
    }
`;

const CellTitle = styled(Typography)`
    font-size: 16px;
    color: ${({ theme }) => theme.palette.text.primary};
`;

const Description = styled(Typography)`
    font-size: 13px;
    color: grey;
`;

const StyledCell = styled("div")`
    display: flex;
    flex-direction: column;
    align-items: start;
`;

const CodesCell = styled("div")`
    display: flex;
    gap: ${({ theme }) => theme.spacing(0.5)};
    align-items: center;

    font-size: 13px;
`;

const SearchField = styled(TextField)`
    width: 250px;
`;

const columns: GridColDef[] = [
    {
        field: "name",
        headerName: "Name",
        flex: 0.4,
        minWidth: 300,
        renderCell: (params: GridRenderCellParams) => (
            <StyledCell>
                <CellTitle>{params.row.name}</CellTitle>
                <Description>{params.row.description}</Description>
            </StyledCell>
        ),
    },
    {
        field: "codes",
        headerName: "Codes",
        flex: 1,
        minWidth: 300,
        valueGetter: (params: GridValueGetterParams) => {
            const codes = params.row.discountCodes.slice(0, 2);
            const moreItems =
                params.row.discountCodes.length > 2
                    ? params.row.discountCodes.length - 2
                    : 0;

            return { codes, moreItems };
        },
        renderCell: (
            params: GridRenderCellParams<{
                codes: IDiscountCode[];
                moreItems: number;
            }>,
        ) => (
            <CodesCell>
                {params.value.codes.map(
                    (code: IDiscountCode, index: number) => {
                        return (
                            <Chip label={code.code} size="small" key={index} />
                        );
                    },
                )}
                {params.value.moreItems > 0 && (
                    <Chip
                        label={`+${params.value.moreItems} more`}
                        size="small"
                    />
                )}
            </CodesCell>
        ),
    },
    {
        field: "category",
        headerName: "Category",
        flex: 0.15,
        minWidth: 150,
        valueGetter: (params: GridValueGetterParams) =>
            PriceRuleCategoryNames[
                params.row.category as PriceRuleCategoryType
            ],
        renderCell: (params: GridRenderCellParams<Date>) => (
            <Chip label={params.value} variant="outlined" />
        ),
    },
    {
        field: "status",
        headerName: "Status",
        flex: 0.5,
        minWidth: 140,
        maxWidth: 150,
        renderCell: (params: GridRenderCellParams) => (
            <StatusChip
                label={
                    priceRuleStatusNames[params.row.status as PriceRuleStatus]
                }
                value={params.row.status}
            />
        ),
    },
    {
        field: "createdBy",
        headerName: "Created by",
        flex: 0.2,
        minWidth: 150,
        valueGetter: (params: GridValueGetterParams) => {
            const createdBy = params.row.createdBy;
            return createdBy
                ? `${createdBy.firstName} ${createdBy.lastName}`
                : "NA";
        },
    },
    {
        field: "createdAt",
        headerName: "Created",
        flex: 0.2,
        minWidth: 150,
        valueGetter: (params: GridValueGetterParams) =>
            `${formatDistance(params.row.createdAt, new Date())} ago`,
    },
    {
        field: "updatedAt",
        headerName: "Last updated",
        flex: 0.2,
        minWidth: 150,
        valueGetter: (params: GridValueGetterParams) =>
            `${formatDistance(params.row.updatedAt, new Date())} ago`,
    },
];

export const DiscountsScreen: FunctionComponent = (): ReactElement => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedPriceRuleIds, setSelectedPriceRuleIds] = useState<string[]>(
        [],
    );
    const [searchInput, setSearchInput] = useState<string>(
        searchParams.get("search") || "",
    );
    const [searchQuery, setSearchQuery] = useState<string>(
        searchParams.get("search") || "",
    );
    const [filterDialogOpen, setFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState({
        status: searchParams.get("status")
            ? Number(searchParams.get("status"))
            : undefined,
        category: searchParams.get("category")
            ? Number(searchParams.get("category"))
            : undefined,
        sortBy: searchParams.get("sortBy") || "createdAt",
        sortOrder: searchParams.get("sortOrder") || "DESC",
    });
    const [page, setPage] = useState<IPage>({
        limit: 20,
        cursor: null,
        direction: "after",
    });

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchQuery(searchInput);
            const params: any = {};
            if (searchInput) params.search = searchInput;
            if (filters.status !== undefined)
                params.status = filters.status.toString();
            if (filters.category !== undefined)
                params.category = filters.category.toString();
            if (filters.sortBy) params.sortBy = filters.sortBy;
            if (filters.sortOrder) params.sortOrder = filters.sortOrder;
            setSearchParams(params);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchInput, filters, setSearchParams]);

    const fetchAllPriceRule = useGetAllPriceRule(
        [
            "priceRules",
            page.limit.toString(),
            page.cursor ?? "",
            page.direction,
            searchQuery,
            filters.status?.toString() ?? "",
            filters.category?.toString() ?? "",
            filters.sortBy,
            filters.sortOrder,
        ],
        {
            limit: page.limit,
            cursor: page.cursor,
            direction: page.direction,
            search: searchQuery || undefined,
            status: filters.status,
            category: filters.category,
            sortBy: filters.sortBy,
            sortOrder: filters.sortOrder,
        },
    );
    const deletePriceRule = useDeletePriceRule("priceRule");

    const handleRowClick = useCallback(
        (params: GridRowParams) => {
            const priceRuleId = params.row.id;
            navigate(`/discounts/${priceRuleId}`);
        },
        [navigate],
    );

    const handleCloseSnackbar = useCallback(
        (
            event: Event | React.SyntheticEvent<any, Event>,
            reason: SnackbarCloseReason,
        ) => {
            if (reason !== "clickaway") {
                setSelectedPriceRuleIds([]);
            }
        },
        [],
    );

    const handleClose = useCallback(() => {
        setSelectedPriceRuleIds([]);
    }, []);

    const handleNavigateBack = useCallback(() => {
        setPage({
            ...page,
            cursor: fetchAllPriceRule.data?.cursors?.previous ?? "",
            direction: "before",
        });
    }, [fetchAllPriceRule.data?.cursors?.previous, page]);

    const handleNavigateForward = useCallback(() => {
        setPage({
            ...page,
            cursor: fetchAllPriceRule.data?.cursors?.next ?? "",
            direction: "after",
        });
    }, [fetchAllPriceRule.data?.cursors?.next, page]);

    const handleApplyFilters = useCallback(
        (newFilters: any) => {
            setFilters(newFilters);
            const params: any = {};
            if (searchQuery) params.search = searchQuery;
            if (newFilters.status !== undefined)
                params.status = newFilters.status.toString();
            if (newFilters.category !== undefined)
                params.category = newFilters.category.toString();
            if (newFilters.sortBy) params.sortBy = newFilters.sortBy;
            if (newFilters.sortOrder) params.sortOrder = newFilters.sortOrder;
            setSearchParams(params);
        },
        [searchQuery, setSearchParams],
    );

    const handleClearFilters = useCallback(() => {
        const clearedFilters = {
            status: undefined,
            category: undefined,
            sortBy: "createdAt",
            sortOrder: "DESC",
        };
        setFilters(clearedFilters);
        const params: any = {};
        if (searchQuery) params.search = searchQuery;
        setSearchParams(params);
    }, [searchQuery, setSearchParams]);

    const handleRemoveFilter = useCallback(
        (filterKey: string) => {
            const newFilters = { ...filters };
            if (filterKey === "status" || filterKey === "category") {
                newFilters[filterKey] = undefined;
            }
            setFilters(newFilters);
            const params: any = {};
            if (searchQuery) params.search = searchQuery;
            if (newFilters.status !== undefined)
                params.status = newFilters.status.toString();
            if (newFilters.category !== undefined)
                params.category = newFilters.category.toString();
            if (newFilters.sortBy) params.sortBy = newFilters.sortBy;
            if (newFilters.sortOrder) params.sortOrder = newFilters.sortOrder;
            setSearchParams(params);
        },
        [filters, searchQuery, setSearchParams],
    );

    const handleDeletePriceRule = useCallback(() => {
        for (const priceRuleId of selectedPriceRuleIds) {
            deletePriceRule.mutate(
                { id: priceRuleId },
                {
                    onSuccess: () => {
                        queryClient.invalidateQueries([
                            "priceRules",
                            page.limit.toString(),
                            page.cursor ?? "",
                            page.direction,
                            searchQuery,
                            filters.status?.toString() ?? "",
                            filters.category?.toString() ?? "",
                            filters.sortBy,
                            filters.sortOrder,
                        ]);
                    },
                },
            );
        }
    }, [
        deletePriceRule,
        page.cursor,
        page.direction,
        page.limit,
        queryClient,
        selectedPriceRuleIds,
        searchQuery,
        filters,
    ]);

    return (
        <Root>
            <TitleContainer>
                <Title>Discounts</Title>
                <SearchContainer>
                    <SearchField
                        size="small"
                        placeholder="Search by name or code"
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.target.value)}
                    />
                    <Button
                        variant="outlined"
                        startIcon={<FilterList />}
                        onClick={() => setFilterDialogOpen(true)}>
                        Filter
                    </Button>
                    <Link to="/discounts/new">
                        <Button
                            variant="contained"
                            startIcon={<Add fontSize="small" />}>
                            create discount
                        </Button>
                    </Link>
                </SearchContainer>
            </TitleContainer>
            {(filters.status !== undefined ||
                filters.category !== undefined ||
                filters.sortBy !== "createdAt" ||
                filters.sortOrder !== "DESC") && (
                <FiltersContainer>
                    {filters.status !== undefined && (
                        <Chip
                            label={`Status: ${
                                priceRuleStatusNames[
                                    filters.status as PriceRuleStatus
                                ]
                            }`}
                            onDelete={() => handleRemoveFilter("status")}
                            size="small"
                        />
                    )}
                    {filters.category !== undefined && (
                        <Chip
                            label={`Category: ${
                                PriceRuleCategoryNames[
                                    filters.category as PriceRuleCategoryType
                                ]
                            }`}
                            onDelete={() => handleRemoveFilter("category")}
                            size="small"
                        />
                    )}
                    {(filters.sortBy !== "createdAt" ||
                        filters.sortOrder !== "DESC") && (
                        <Chip
                            label={`Sort: ${
                                filters.sortBy === "createdAt"
                                    ? "Created Date"
                                    : filters.sortBy === "status"
                                    ? "Status"
                                    : filters.sortBy === "category"
                                    ? "Category"
                                    : "Name"
                            } (${
                                filters.sortOrder === "ASC"
                                    ? "Ascending"
                                    : "Descending"
                            })`}
                            size="small"
                        />
                    )}
                    <Button size="small" onClick={handleClearFilters}>
                        Clear All
                    </Button>
                </FiltersContainer>
            )}
            {fetchAllPriceRule?.data?.records.length === 0 && (
                <EmptyRecords>
                    <EmptyRecordsContainer>
                        <Title>Manage discounts and promotions</Title>
                        <div>
                            Create discount codes and automatic discounts that
                            apply at checkout.
                        </div>
                        <Link to="/discounts/new">
                            <Button variant="contained">create discount</Button>
                        </Link>
                    </EmptyRecordsContainer>
                </EmptyRecords>
            )}

            {fetchAllPriceRule.data &&
                (fetchAllPriceRule.data?.records ?? []).length > 0 && (
                    <>
                        <StyledDataGrid
                            rows={fetchAllPriceRule?.data?.records ?? []}
                            columns={columns}
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: 20,
                                    },
                                },
                            }}
                            pageSizeOptions={[10, 20, 30]}
                            disableRowSelectionOnClick={true}
                            onRowClick={handleRowClick}
                            checkboxSelection={true}
                            rowSelectionModel={selectedPriceRuleIds}
                            onRowSelectionModelChange={(
                                newSelection: GridRowSelectionModel,
                            ) => {
                                setSelectedPriceRuleIds(
                                    newSelection as string[],
                                );
                            }}
                            components={{
                                Footer: () => (
                                    <DataGridFooter
                                        hasNextRecords={Boolean(
                                            fetchAllPriceRule.data.cursors.next,
                                        )}
                                        hasPreviousRecords={Boolean(
                                            fetchAllPriceRule.data.cursors
                                                .previous,
                                        )}
                                        onNavigateBack={handleNavigateBack}
                                        onNavigateForward={
                                            handleNavigateForward
                                        }
                                    />
                                ),
                            }}
                        />
                        <Snackbar
                            open={selectedPriceRuleIds.length > 0}
                            autoHideDuration={null}
                            onClose={handleCloseSnackbar}
                            message={`${selectedPriceRuleIds.length} discount${
                                selectedPriceRuleIds.length > 1 ? "s" : ""
                            } selected`}
                            ClickAwayListenerProps={{
                                mouseEvent: false,
                                touchEvent: false,
                            }}
                            action={
                                <>
                                    <Button onClick={handleDeletePriceRule}>
                                        Delete
                                    </Button>
                                    <IconButton onClick={handleClose}>
                                        <Close />
                                    </IconButton>
                                </>
                            }
                        />
                    </>
                )}
            <FilterDialog
                open={filterDialogOpen}
                onClose={() => setFilterDialogOpen(false)}
                filters={filters}
                onApply={handleApplyFilters}
            />
        </Root>
    );
};
